"use client";

import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Typography } from "../ui/typography";
import { MainImageSwiper } from "./MainImageSwiper";
import { ThumbnailSwiper } from "./ThumbnailSwiper";
import { Button } from "../ui/button";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon } from "@hugeicons/core-free-icons";
import { PaginationCounter } from "./PaginationCounter";
import { Skeleton } from "../ui/skeleton";
import { AccommodationImage } from "@/types/accommodations";

interface ImageGalleryModalProps {
  images: AccommodationImage[];
  isOpen: boolean;
  onClose: () => void;
  initialIndex?: number;
}

function GallerySkeleton() {
  return (
    <div className="flex flex-col gap-4 w-full">
      <Skeleton className="w-full aspect-[3/2] max-h-[calc(100dvh-220px)] md:max-h-[380px] rounded-lg" />
      <div className="flex gap-4 justify-center">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton
            key={index}
            className="w-[106px] md:w-[121px] h-[96px] md:h-[100px] rounded-lg shrink-0"
          />
        ))}
      </div>
    </div>
  );
}

export function ImageGalleryModal({
  images,
  isOpen,
  onClose,
  initialIndex = 0,
}: ImageGalleryModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      // Small delay to ensure smooth modal opening
      const timer = setTimeout(() => setIsLoading(false), 100);
      return () => clearTimeout(timer);
    } else {
      setIsLoading(true);
    }
  }, [isOpen, initialIndex]);

  const handleSlideChange = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const handleThumbnailClick = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="flex flex-col gap-0 p-0 overflow-hidden [&>button]:hidden max-md:inset-0 max-md:top-0 max-md:left-0 max-md:translate-x-0 max-md:translate-y-0 max-md:max-w-none max-md:w-full max-md:h-dvh max-md:rounded-none max-md:border-0 md:max-w-5xl md:max-h-[95vh]"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">گالری تصاویر</DialogTitle>

        <div className="">
          <Button
            variant={"ghost"}
            className="mb-0 mt-3.5 mx-3 md:mx-[46px] self-start"
            onClick={onClose}
          >
            <Typography className="hidden md:block text-[18px] font-normal">
              بستن
            </Typography>
            <HugeiconsIcon
              icon={Cancel01Icon}
              className="size-[22px] md:hidden"
              strokeWidth={1.5}
            />
          </Button>
        </div>

        <div className="w-full overflow-hidden flex flex-col h-full px-6 md:px-10">
          {isLoading ? (
            <GallerySkeleton />
          ) : (
            <>
              {/* Main Image Display */}
              <MainImageSwiper
                images={images}
                currentIndex={currentIndex}
                onSlideChange={handleSlideChange}
              />

              <PaginationCounter
                current={currentIndex + 1}
                total={images.length}
                className="text-sm font-medium text-black-500 bg-transparent static text-center md:hidden mb-8"
              />

              {/* Thumbnails */}
              {images.length > 1 && (
                <ThumbnailSwiper
                  images={images}
                  currentIndex={currentIndex}
                  onThumbnailClick={handleThumbnailClick}
                />
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
